// Daily view derivations shared by web and mobile.
// Task due_date and focus_date are calendar dates (YYYY-MM-DD) in the user's
// local calendar. They are never parsed as UTC instants.

import type { Capture, Classification, ItemKind, Task } from './types';
import { formatInstantClock } from './clock.ts';

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** The local calendar date of `date` as YYYY-MM-DD. */
export function localDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD as local midnight. `new Date('2026-09-12')` would be UTC midnight, the previous day in the Americas. */
export function parseCalendarDate(value: string): Date {
  const match = CALENDAR_DATE.exec(value);
  if (!match) {
    throw new Error(`Not a calendar date (YYYY-MM-DD): ${value}`);
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** A real calendar date in YYYY-MM-DD form: rejects 2026-02-30, not just a malformed shape. */
export function isCalendarDate(value: unknown): value is string {
  return typeof value === 'string' && CALENDAR_DATE.test(value) && localDateString(parseCalendarDate(value)) === value;
}

/** Whole calendar days from `from` to `to` (both YYYY-MM-DD); negative when `to` is earlier. */
export function calendarDaysBetween(from: string, to: string): number {
  return Math.round((parseCalendarDate(to).getTime() - parseCalendarDate(from).getTime()) / DAY_MS);
}

/** "Sat, Sep 12" for a YYYY-MM-DD calendar date. */
export function formatCalendarDate(value: string): string {
  return parseCalendarDate(value).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// Today in GTD terms lives in gtd.ts, which generate-digest imports too.
export { bucketTasksForToday, gtdToday } from './gtd.ts';
export type { ContextGroup, GtdToday, GtdTodayTask, TodayBuckets, TodayTask } from './gtd.ts';

/**
 * The line under Due naming the focused tasks that are due (gtdToday's
 * dueInFocus), or null when there are none: "In Focus above: Call the bank
 * (due today); Pay rent (overdue)".
 */
export function dueInFocusLine(tasks: readonly Pick<Task, 'name' | 'due_date'>[], today: string): string | null {
  if (tasks.length === 0) return null;
  const named = tasks.map(t => `${t.name} (${t.due_date !== null && t.due_date < today ? 'overdue' : 'due today'})`);
  return `In Focus above: ${named.join('; ')}`;
}

export interface DigestFreshness {
  isToday: boolean;
  /** Calendar days since generation (0 = today). */
  daysOld: number;
  /** Human label that always names the generation date. */
  label: string;
}

/** Describe when a digest was generated so an old digest is never presented as today's. */
export function digestFreshness(generatedAt: string, now: Date = new Date()): DigestFreshness {
  const generated = new Date(generatedAt);
  if (Number.isNaN(generated.getTime())) {
    throw new Error(`Invalid digest timestamp: ${generatedAt}`);
  }
  const daysOld = calendarDaysBetween(localDateString(generated), localDateString(now));
  const time = formatInstantClock(generated);
  const date = generated.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (daysOld === 0) return { isToday: true, daysOld, label: `Generated today at ${time}` };
  if (daysOld === 1) return { isToday: false, daysOld, label: `Generated yesterday (${date}) at ${time}` };
  return { isToday: false, daysOld, label: `Generated ${date} at ${time} — ${daysOld} days old` };
}

export type FilingState =
  /** Classification is still running (recent, no result or error yet). */
  | 'filing'
  /** Filed as a task or note. */
  | 'filed'
  /** AI was unsure; the user should confirm or correct. */
  | 'needs_review'
  /** AI filing failed; the capture is saved but unfiled. */
  | 'failed'
  /** Saved, never filed. */
  | 'unfiled';

const FILED_STATUSES = new Set(['auto_filed', 'manually_filed', 'manually_corrected']);
const FILING_WINDOW_MS = 2 * 60 * 1000;

/** Where a capture stands. `classification` is the capture's latest classification, if any. */
export function captureFilingState(
  capture: Pick<Capture, 'processed' | 'processing_error' | 'created_at'>,
  classification: Pick<Classification, 'status'> | null | undefined,
  now: Date = new Date(),
): FilingState {
  if (classification) {
    if (FILED_STATUSES.has(classification.status)) return 'filed';
    if (classification.status === 'needs_review') return 'needs_review';
  }
  if (capture.processing_error) return 'failed';
  if (!capture.processed && now.getTime() - new Date(capture.created_at).getTime() < FILING_WINDOW_MS) {
    return 'filing';
  }
  return 'unfiled';
}

/** A capture's most recent classification (latest `created_at`), or null when it has none. */
export function latestClassification(capture: {
  classifications: readonly Classification[] | null;
}): Classification | null {
  let latest: Classification | null = null;
  for (const classification of capture.classifications ?? []) {
    if (!latest || Date.parse(classification.created_at) > Date.parse(latest.created_at)) {
      latest = classification;
    }
  }
  return latest;
}

/** The task or note a capture was filed as, from its latest classification; null until it is filed. */
export function filedItem(
  classification: Pick<Classification, 'status' | 'category' | 'destination_id'> | null | undefined,
): { kind: ItemKind; id: string } | null {
  if (!classification || !FILED_STATUSES.has(classification.status) || !classification.destination_id) return null;
  const kind = classification.category;
  return kind === 'task' || kind === 'note' ? { kind, id: classification.destination_id } : null;
}

/**
 * Home's line for the captures not filed yet (neither filed nor waiting for
 * review): "1 capture not filed yet", "3 captures not filed yet". Null for
 * none, like every other count on Home.
 */
export function unfiledCaptureText(count: number): string | null {
  if (count <= 0) return null;
  return `${count} ${count === 1 ? 'capture' : 'captures'} not filed yet`;
}

/** A capture the Inbox may send through AI filing again: its auto-filing failed or never finished. */
export function canRetryAutoFiling(state: FilingState): boolean {
  return state === 'failed' || state === 'unfiled';
}

const TITLE_LIMIT = 120;

/** A capture's first line, shortened to a title. */
export function captureTitle(rawText: string): string {
  const firstLine = rawText.trim().split('\n')[0].trim();
  return firstLine.length > TITLE_LIMIT ? `${firstLine.slice(0, TITLE_LIMIT - 1)}…` : firstLine;
}
